import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { binarize, inkBBox, rgbaToGray, type BBox } from "./core/bitmap";
import { TraceClient } from "./core/trace-client";
import type { TraceSuccess } from "./core/worker-protocol";
import type { Mode } from "./core/presets";
import type { OutlineOptions } from "./core/pipeline";

export interface Source {
  gray: Uint8Array;
  width: number;
  height: number;
  /**
   * Size of the DRAWING, not of the canvas.
   *
   * No son lo mismo y confundirlos rompe el dimensionado: la rama de hojas llega
   * como un JPEG de 740×740 —cuadrado— con un dibujo vertical de 280×670 adentro.
   * Looking at the canvas picks the wrong side to pin.
   */
  contentWidth: number;
  contentHeight: number;
  fileName: string;
}

/** Decodes a file to grey. Composites over white, so a transparent PNG does not read as ink. */
export async function decode(file: File): Promise<Source> {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no se pudo abrir un contexto 2D");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, bitmap.width, bitmap.height);
    ctx.drawImage(bitmap, 0, 0);
    const { data } = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    const gray = rgbaToGray(data, bitmap.width, bitmap.height);
    const bb = inkBBox(binarize(gray, bitmap.width, bitmap.height));
    return {
      gray,
      width: bitmap.width,
      height: bitmap.height,
      contentWidth: bb ? bb.x1 - bb.x0 : bitmap.width,
      contentHeight: bb ? bb.y1 - bb.y0 : bitmap.height,
      fileName: file.name,
    };
  } finally {
    bitmap.close();
  }
}

export interface TraceParams {
  mode: Mode;
  side: "width" | "height";
  mm: number;
  rotation: 0 | 90 | 180 | 270;
  /** null = no engrosar; undefined = usar el default del modo */
  targetMm: number | null | undefined;
  /** areas covered before tracing, in pixels of the original image */
  erase: BBox[];
  /** outline only; null = off. Memoise it: it is a dependency by identity */
  outline: OutlineOptions | null;
}

/**
 * Traza con debounce en un Web Worker.
 *
 * The worker is what keeps the UI alive: morphology over a large image is
 * hundreds of milliseconds, and on the main thread the slider felt stuck.
 */
export function useImageTrace(
  source: Source | null,
  params: TraceParams,
  delay = 180,
  /** besides the result, trace the UNTHICKENED version so the two can be compared */
  withBaseline = false,
) {
  const [result, setResult] = useState<TraceSuccess | null>(null);
  const [baseline, setBaseline] = useState<TraceSuccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const client = useMemo(() => new TraceClient(), []);
  useEffect(() => () => client.dispose(), [client]);

  const runId = useRef(0);

  const run = useCallback(async () => {
    if (!source) return;
    const id = ++runId.current;
    setBusy(true);
    const base = {
      mode: params.mode,
      rotation: params.rotation,
      ...(params.side === "width" ? { mmWidth: params.mm } : { mmHeight: params.mm }),
      ...(params.erase.length ? { erase: params.erase } : {}),
      ...(params.outline ? { outline: params.outline } : {}),
    } as const;
    try {
      const [r, b] = await Promise.all([
        client.trace(source.gray, source.width, source.height, {
          ...base,
          ...(params.targetMm !== undefined ? { targetMm: params.targetMm } : {}),
        }),
        // The reference goes UNTHICKENED: that is what shows which detail is lost.
        withBaseline
          ? client.trace(source.gray, source.width, source.height, { ...base, targetMm: null })
          : Promise.resolve(null),
      ]);
      // Discard results from superseded runs: without this, moving the slider fast
      // puede pintar un resultado viejo encima del nuevo.
      if (id === runId.current) {
        setResult(r);
        setBaseline(b);
        setError(null);
      }
    } catch (e) {
      if (id === runId.current) {
        setError(e instanceof Error ? e.message : String(e));
        setResult(null);
        setBaseline(null);
      }
    } finally {
      if (id === runId.current) setBusy(false);
    }
  }, [client, source, params.mode, params.side, params.mm, params.rotation,
      params.targetMm, params.erase, params.outline, withBaseline]);

  useEffect(() => {
    if (!source) {
      setResult(null);
      setBaseline(null);
      setError(null);
      return;
    }
    const t = setTimeout(run, delay);
    return () => clearTimeout(t);
  }, [run, source, delay]);

  return { result, baseline, error, busy };
}
